import { useAppSelector } from '@/store/store-hooks'
import { memo } from 'react'
import AgencyDocumentViewer from '@/agent-v2/agency-documents/components/AgencyDocumentViewer'
import ContentViewerHeader, { type ContentViewerHeaderVariant } from '@/content-viewer/components/common/ContentViewerHeader'
import { CaseStatusInfoTag } from '@/citator/components/CaseStatusInfoTag'
import { LrrContentStatusInfoTag } from '@/agent-v2/references/LrrContentStatusInfoTag'
import { isDisplayableLrrContentStatus } from '@/agent-v2/references/lrr-content-status'
import { isDisplayableCaseStatus } from '@/citator/schema/case-status-schema'
import { CitatorSurface } from '@/analytics/enums/citator.enums'
import { RenderFormat, ViewerDocumentKind, ViewerOpenContextKind, type ContentViewerSession } from '../store/schemas'
import { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import UserFileContentRenderer from '@/user_files/files/components/UserFileContentRenderer'
import CaseLawRenderer from './agent-reference/renderers/CaseLawRenderer'
import LegalResearchReportRenderer from './agent-reference/renderers/LegalResearchReportRenderer'
import { useAgentV2CitationFootnoteNumber } from '@/agent-v2/hooks/useAgentV2CitationFootnoteNumber'
import { useDraftSourceFootnoteNumber } from '@/drafting/workspace/hooks/useDraftSourceFootnoteNumber'
import { createRiaContext } from '@/analytics/utils/ria-source-analytics'
import { selectAgentV2ConversationMatterId } from '@/agent-v2/conversations/store/selectors'
import { selectActiveContentViewerSession } from '../store/selectors'
import MedSummaryConvertedPdfRenderer from '@/medical-billing-summaries/sources/MedSummaryConvertedPdfRenderer'
import { TableDocketRecordViewer } from '@/tables/components/TableDocketRecordViewer'
import { AgentV2DocketRecordViewer } from '@/agent-v2/references/AgentV2DocketRecordViewer'

// Stable empty array references to prevent memoization breaks
const EMPTY_PDF_HIGHLIGHTS: PaxtonHighlightArea[] = []
const EMPTY_MARKDOWN_HIGHLIGHTS: string[] = []

type ContentViewerConstructorProps = {
  conversationId: string
  headerVariant?: ContentViewerHeaderVariant
}
/**
 * Content Viewer Constructor
 *
 * Routes normalized viewer sessions to the correct layout and renderer.
 */
function ContentViewerConstructor(props: ContentViewerConstructorProps) {
  const { conversationId, headerVariant } = props

  const contentViewerSession = useAppSelector(selectActiveContentViewerSession)
  const chatCitationContext = contentViewerSession?.context.kind === ViewerOpenContextKind.CHAT_CITATION ? contentViewerSession.context : null
  const agentV2FootnoteNumber = useAgentV2CitationFootnoteNumber({
    conversationId: chatCitationContext?.conversationId,
    turnId: chatCitationContext?.turnId,
    citationId: chatCitationContext?.citationId,
  })
  // In the drafting workspace's source tab, the footnote number corresponds to the draft's citation, not the chat's.
  const draftFootnoteNumber = useDraftSourceFootnoteNumber({
    conversationId: chatCitationContext?.conversationId,
    citationId: chatCitationContext?.citationId,
  })
  const footnoteNumber =
    headerVariant === 'source-tab' || headerVariant === 'compact-source-tab' ? draftFootnoteNumber ?? undefined : agentV2FootnoteNumber ?? undefined
  const sessionMatterId = useAppSelector((state) => {
    const matterConversationId =
      chatCitationContext?.conversationId ?? (contentViewerSession?.document.kind === ViewerDocumentKind.CASELAW ? conversationId : null)
    if (!matterConversationId) return null
    return selectAgentV2ConversationMatterId(state, { conversationId: matterConversationId })
  })

  if (!contentViewerSession) {
    return null
  }

  return renderSessionContent({
    session: contentViewerSession,
    footnoteNumber,
    matterId: sessionMatterId,
    headerVariant,
  })
}

export default memo(ContentViewerConstructor)

function renderSessionContent(args: {
  session: ContentViewerSession
  footnoteNumber?: number
  matterId: string | null
  headerVariant?: ContentViewerHeaderVariant
}) {
  const { session, footnoteNumber, matterId, headerVariant } = args
  const { document, presentation } = session
  const chatCitationContext = session.context.kind === ViewerOpenContextKind.CHAT_CITATION ? session.context : null

  switch (document.kind) {
    case ViewerDocumentKind.AGENCY:
      return (
        <div className="bg-brand-neutral-50 h-full w-full flex flex-col overflow-hidden">
          <ContentViewerHeader title={document.title} variant={headerVariant} />
          <AgencyDocumentViewer
            key={document.referenceId}
            conversationId={document.conversationId}
            tableId={document.tableId}
            referenceId={document.referenceId}
          />
        </div>
      )
    case ViewerDocumentKind.USER_FILE: {
      const { renderAsset } = document
      const title = getHeaderTitle(session, document.title, footnoteNumber)
      const renderer = (
        <UserFileContentRenderer
          userFileId={document.userFileId}
          format={renderAsset?.format}
          pdfHighlights={presentation?.pdfHighlights ?? EMPTY_PDF_HIGHLIGHTS}
          markdownHighlights={presentation?.markdownHighlights ?? EMPTY_MARKDOWN_HIGHLIGHTS}
          startingPage={presentation?.startingPage}
          pdfExcerpt={presentation?.pdfExcerpt}
          title={title}
        />
      )

      // Without a declared renderAsset the renderer resolves the format at load time and
      // owns the markdown scroll wrapper itself.
      return (
        <div className="bg-brand-neutral-50 h-full w-full flex flex-col overflow-hidden">
          <ContentViewerHeader title={title} variant={headerVariant} />
          {renderAsset === undefined || renderAsset.format === RenderFormat.PDF ? (
            renderer
          ) : (
            <div className="flex-1 w-full h-full overflow-y-auto pb-4">{renderer}</div>
          )}
        </div>
      )
    }

    case ViewerDocumentKind.CASELAW: {
      const title = getHeaderTitle(session, document.title, footnoteNumber)
      const caseStatus = document.caseStatus
      const badge = isDisplayableCaseStatus(caseStatus) ? (
        <CaseStatusInfoTag status={caseStatus} checkedLabelSize="default" surface={CitatorSurface.Chat} trackImpression={false} />
      ) : undefined
      const riaContext = chatCitationContext ? createRiaContext(chatCitationContext.conversationId, chatCitationContext.turnId, matterId) : null

      return (
        <div className="bg-brand-neutral-50 h-full w-full flex flex-col overflow-hidden">
          <ContentViewerHeader title={title} badge={badge} variant={headerVariant} />
          <CaseLawRenderer
            s3_path={document.s3Path}
            highlightSentences={presentation?.markdownHighlights}
            parentId={document.parentId}
            riaContext={riaContext}
            viewerContext={{ conversationId: riaContext?.conversationId, messageId: riaContext?.messageId, matterId }}
            defaultTab={presentation?.defaultTab}
            defaultOpenSection={presentation?.defaultOpenSection}
            resetKey={chatCitationContext?.referenceId}
          />
        </div>
      )
    }

    case ViewerDocumentKind.LRR: {
      const title = getHeaderTitle(session, document.title, footnoteNumber)
      const contentStatus = document.contentStatus
      const badge = isDisplayableLrrContentStatus(contentStatus) ? <LrrContentStatusInfoTag status={contentStatus} /> : undefined

      return (
        <div className="bg-brand-neutral-50 h-full w-full flex flex-col overflow-hidden">
          <ContentViewerHeader title={title} badge={badge} variant={headerVariant} />
          <LegalResearchReportRenderer s3_path={document.s3Path} highlightSentences={presentation?.markdownHighlights} />
        </div>
      )
    }

    case ViewerDocumentKind.MED_SUMMARY_CONVERTED_PDF: {
      const title = getHeaderTitle(session, document.title, footnoteNumber)

      return (
        <div className="bg-brand-neutral-50 h-full w-full flex flex-col overflow-hidden">
          <ContentViewerHeader title={title} variant={headerVariant} />
          <MedSummaryConvertedPdfRenderer
            assetId={document.assetId}
            userFileId={document.userFileId}
            title={document.title}
            pdfHighlights={presentation?.pdfHighlights ?? EMPTY_PDF_HIGHLIGHTS}
            startingPage={presentation?.startingPage}
          />
        </div>
      )
    }

    // A docket's saved record renders like a matter file's markdown, built from the stored
    // reference instead of a fetched file.
    case ViewerDocumentKind.DOCKET: {
      const title = getHeaderTitle(session, document.title, footnoteNumber)

      return (
        <div className="bg-brand-neutral-50 h-full w-full flex flex-col overflow-hidden">
          <ContentViewerHeader title={title} variant={headerVariant} />
          <div className="flex-1 w-full h-full overflow-y-auto pb-4">
            {document.tableId !== undefined ? (
              <TableDocketRecordViewer key={`${document.tableId}:${document.referenceId}`} tableId={document.tableId} referenceId={document.referenceId} />
            ) : (
              <AgentV2DocketRecordViewer
                key={`${document.conversationId}:${document.citationId}`}
                conversationId={document.conversationId}
                citationId={document.citationId}
              />
            )}
          </div>
        </div>
      )
    }
  }
}

function getHeaderTitle(session: ContentViewerSession, title: string, footnoteNumber?: number) {
  if (session.context.kind !== ViewerOpenContextKind.CHAT_CITATION || !footnoteNumber) return title
  return `[${footnoteNumber}] ${title}`
}
